import { PresenceStatus } from "../constants/presenceStatus"

export interface Practice {
  id: string
  name: string
  placeName: string
  startTime: string
  endTime: string
  userPresence: PresenceStatus
}

export interface Festival {
  id: string
  name: string
  placeName: string
  startTime: string
  endTime: string
  enabled: boolean
  userPresence: PresenceStatus
}

export interface PracticeListResponse {
  practiceList: [Practice]
}

export interface FestivalListResponse {
  festivalList: [Festival]
}

export interface PracticePresenceResponse {
  practicePresence: {
    practice: {
      id: string
      name: string
      placeName: string
      startTime: string
      endTime: string
    }
    members: {
      name: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }[]
    authorizedUserPresence: {
      name: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }
    guestList: {
      guestId: string
      guestName: string
      guestEmail: string
      memo: string
      updatedAt: string
      creatorName: string
    }[]
  }
}

export interface PutPracticePresenceData {
  practiceId: string
  userEmail: string
  presenceStatus: string
  presenceMemo: string
}

export interface PutFestivalPresenceData {
  festivalId: string
  userEmail: string
  presenceStatus: string
  presenceMemo: string
}

export interface NewPracticeGuestData {
  practiceId: string
  guestEmail: string
  guestName: string
  memo: string
}

export interface UpdatePracticeGuestData {
  practiceId: string
  guestId: string
  guestEmail: string
  guestName: string
  memo: string
}

export interface DeletePracticeGuestData {
  practiceId: string
  guestId: string
}

export interface InternalPracticePresenceResponse {
  practicePresence: {
    practice: {
      name: string
      placeName: string
      startTime: string
      endTime: string
    }
    members: {
      name: string
      email: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }[]
    guestList: {
      guestId: string
      guestName: string
      guestEmail: string
      memo: string
      updatedAt: string
      creatorName: string
    }[]
  }
}

export interface FestivalPresenceResponse {
  festivalPresence: {
    festival: {
      id: string
      name: string
      placeName: string
      startTime: string
      endTime: string
    }
    presentMembers: {
      name: string
      updatedAt: string
    }[]
    absentMembers: {
      name: string
      updatedAt: string
    }[]
    members: {
      name: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }[]
    authorizedUserPresence: {
      name: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }
  }
}

export interface RefreshTokenResponse {
  access_token: string
  expires_in: number
  scope: string
  token_type: string
}

export interface DeletePracticeData {
  teamId: string
  practiceId: string
}

export interface DeleteFestivalData {
  teamId: string
  festivalId: string
}

// Shape of a festival sent to the CREATE endpoint (POST) — backend generates the id
export interface PostFestivalInput {
  name: string
  startTime: string   // ISO 8601 string
  endTime: string     // ISO 8601 string
  placeName: string
  enabled: boolean
}

export interface PostFestivalData {
  teamId: string
  festival: PostFestivalInput
}

// Shape of a festival sent to the UPDATE endpoint (PUT) — id and updatedAt required
export interface FestivalInput {
  id: string
  name: string
  startTime: string   // ISO 8601 string
  endTime: string     // ISO 8601 string
  placeName: string
  enabled: boolean
  updatedAt: string   // ISO 8601 string
}

export interface PutFestivalData {
  teamId: string
  festival: FestivalInput
}

export interface InternalFestivalPresenceResponse {
  festivalPresence: {
    festival: {
      name: string
      placeName: string
      startTime: string
      endTime: string
    }
    members: {
      name: string
      email: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }[]
  }
}

// A single practice as returned by GET /api/internal/practices — includes enabled field
export interface InternalPractice {
  id: string
  name: string
  placeName: string
  startTime: string
  endTime: string
  enabled: boolean
}

// Reason: GET /api/internal/practices returns a flat JSON array, not a wrapped object
export type InternalPracticeListResponse = InternalPractice[]

// A single festival as returned by GET /api/internal/festivals — no userPresence field
export interface InternalFestival {
  id: string
  name: string
  placeName: string
  startTime: string
  endTime: string
  enabled: boolean
}

// Reason: GET /api/internal/festivals returns a flat JSON array, not a wrapped object
export type InternalFestivalListResponse = InternalFestival[]

// Shape of a practice sent to the CREATE endpoint (POST) — backend generates the id
export interface PostPracticeInput {
  name: string
  startTime: string   // ISO 8601 string
  endTime: string     // ISO 8601 string
  placeName: string
  enabled: boolean
}

export interface PostPracticeData {
  teamId: string
  practice: PostPracticeInput
}

// Shape of a practice sent to the UPDATE endpoint (PUT) — id and updatedAt required
export interface PracticeInput {
  id: string
  name: string
  startTime: string   // ISO 8601 string
  endTime: string     // ISO 8601 string
  placeName: string
  enabled: boolean
  updatedAt: string   // ISO 8601 string
}

export interface PutPracticeData {
  teamId: string
  practice: PracticeInput
}

const apiUrl = process.env.GATSBY_API_BASE_URL ?? "https://d15wik05ic63zs.cloudfront.net"

export async function getPracticeList(accessToken: string): Promise<PracticeListResponse | null> {
  const url = `${apiUrl}/api/practiceList`
  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch repository: ${response.status}`)
    }

    return await response.json() as PracticeListResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function getPracticePresence(practiceId: string, accessToken: string): Promise<PracticePresenceResponse | null> {
  const url = `${apiUrl}/api/practice?practiceId=${practiceId}`

  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as PracticePresenceResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function putPracticePresence(
  data: PutPracticePresenceData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/practicePresence`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function putFestivalPresence(
  data: PutFestivalPresenceData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/festivalPresence`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}


export async function getInternalPracticePresence(practiceId: string, accessToken: string): Promise<InternalPracticePresenceResponse | null> {
  const url = `${apiUrl}/api/internal/practice?practiceId=${practiceId}`

  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as InternalPracticePresenceResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function getFestivalList(accessToken: string): Promise<FestivalListResponse | null> {
  const url = `${apiUrl}/api/festivalList`
  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch repository: ${response.status}`)
    }

    return await response.json() as FestivalListResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function getFestivalPresence(festivalId: string, accessToken: string): Promise<FestivalPresenceResponse | null> {
  const url = `${apiUrl}/api/festival?festivalId=${festivalId}`

  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as FestivalPresenceResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export interface GoogleToken {
  access_token: string
  expires_in: number
  refresh_token: string
  scope: string
  token_type: string
  id_token: string
}

export interface GoogleTokenResponse {
  google_token: GoogleToken,
  roles: [string]
}

export async function getGoogleToken(code: string, redirectUrl: string): Promise<GoogleTokenResponse | null> {
  const url = `${apiUrl}/api/google/getToken?code=${code}&redirect_url=${redirectUrl}`

  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as GoogleTokenResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function refreshGoogleToken(refreshToken: string): Promise<RefreshTokenResponse | null> {
  const url = `${apiUrl}/api/google/refreshToken?refresh_token=${refreshToken}`

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as RefreshTokenResponse;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
}

export async function putPracticeGuest(
  data: NewPracticeGuestData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/newPracticeGuest`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function updatePracticeGuest(
  data: UpdatePracticeGuestData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/updatePracticeGuest`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function deletePracticeGuest(
  data: DeletePracticeGuestData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/deletePracticeGuest`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

// Creates a new practice via POST /api/internal/practice — backend generates the id
export async function postPractice(
  data: PostPracticeData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/internal/practice`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    // Reason: 204 No Content is a valid success response for creation —
    // attempting response.json() on an empty body throws a SyntaxError.
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true }
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

// Updates an existing practice via PUT /api/internal/practice
export async function putPractice(
  data: PutPracticeData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/internal/practice`
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    // Reason: same as postPractice — 204 No Content is a valid success response
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true }
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

// Deletes a practice via DELETE /api/internal/practice
export async function deletePractice(
  data: DeletePracticeData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/internal/practice`
  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    // Reason: DELETE endpoints commonly return 204 No Content on success
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true }
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

// Fetches admin-level festival presence data (includes member emails)
export async function getInternalFestivalPresence(festivalId: string, accessToken: string): Promise<InternalFestivalPresenceResponse | null> {
  const url = `${apiUrl}/api/internal/festival?festivalId=${festivalId}`

  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as InternalFestivalPresenceResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

// Creates a new festival via POST /api/internal/festival — backend generates the id
export async function postFestival(
  data: PostFestivalData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/internal/festival`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    // Reason: 204 No Content is a valid success response for creation —
    // attempting response.json() on an empty body throws a SyntaxError.
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true }
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

// Updates an existing festival via PUT /api/internal/festival
export async function putFestival(
  data: PutFestivalData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/internal/festival`
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    // Reason: same as postFestival — 204 No Content is a valid success response
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true }
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

// Deletes a festival via DELETE /api/internal/festival
export async function deleteFestival(
  data: DeleteFestivalData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/internal/festival`
  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    // Reason: DELETE endpoints commonly return 204 No Content on success
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true }
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

// Fetches the admin practice list via GET /api/internal/practices.
// The teamId is resolved server-side from the authorizer — no request body needed.
export async function getInternalPracticeList(accessToken: string): Promise<InternalPracticeListResponse | null> {
  const url = `${apiUrl}/api/internal/practices`
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as InternalPracticeListResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

// Fetches the admin festival list via GET /api/internal/festivals.
// The teamId is resolved server-side from the authorizer — no request body needed.
export async function getInternalFestivalList(accessToken: string): Promise<InternalFestivalListResponse | null> {
  const url = `${apiUrl}/api/internal/festivals`
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as InternalFestivalListResponse
  } catch (error) {
    console.error(error)
    return null
  }
}